import Panel from 'beam/dist/Panel'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { CircularProgress, Grid, Theme } from '@mui/material'
import { useEffect, useRef, useState, useContext } from 'react'
import { getComments } from './api'
import { useQuery } from 'react-query'
import { EvalComment } from '../SingleEvaluationForm/evaluationForm.api'
import ErrorBoundary from '../ErrorBoundary'
import BeamIcon from 'beam/dist/BeamIcon'
import { Chat, Message, MessagePin, Participant, SelectedChatBubbleContext } from './constants'
import { ColorScheme } from '../Chat/chat.constants'
import { red } from '@mui/material/colors'
import classNames from 'classnames'
import CommentIcon from './CommentIcon'

export const dataTestIdPrefix = 'MediaPlayerWorkspace-ChatMap-'

// maximum width of a message in the chat map; and multiplier to determine width based on text length
// roughly based on chat window; however the ratio is not exact. based on how it looks.
const MAX_TEXT_WIDTH = 72
const TEXT_RATIO = 0.6

// each line is set as flex basis 100, and blank line is 1/3 ratio as per design
const FLEX_BASIS_LINE_HEIGHT = 100
const FLEX_BASIS_BLANK_LINE_HEIGHT = FLEX_BASIS_LINE_HEIGHT / 3

const useStyles = makeStyles((theme: Theme) => ({
  panelBody: {
    height: `calc(100% - ${theme.spacing(4)})`,
    flexWrap: 'wrap',
  },
  pinsContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: `calc(100% - ${theme.spacing(4)})`,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    position: 'relative',
    width: theme.spacing(10),
    willChange: 'transform',
  },
  loadingContainer: {
    padding: theme.spacing(2),
    maxWidth: '1023px',
    height: '215px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pinRow: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: FLEX_BASIS_LINE_HEIGHT,
  },
  rightAligned: {
    alignSelf: 'flex-end',
    height: '100%',
    borderRadius: '2px 2px 0 2px',
    cursor: 'pointer',
  },
  leftAligned: {
    alignSelf: 'flex-start',
    height: '100%',
    borderRadius: '2px 2px 2px 0px',
    cursor: 'pointer',
  },
  commentIcon: {
    position: 'fixed',
    transform: `translate(calc(${theme.spacing(10)} + 4px), 0)`,
  },
  blankLine: {
    alignSelf: 'center',
    flexGrow: 1,
    flexShrink: 1,
    backgroundColor: 'transparent',
  },
  chatMapPanel: {
    height: '100%',
  },
  isActive: {
    boxShadow: `0 0 0 2px ${red[700]}`,
  },
}))


export const ChatMapPanel
  = ({ ccrId, evalId, chat, agentAcdId, loading }:
     { ccrId: number, evalId: number, chat: Chat | null, agentAcdId: string, loading: boolean }) => {

    const { t } = useTranslation()
    const classes = useStyles()
    const ref = useRef(null)

    const [pins, setPins] = useState<MessagePin[]>([])
    const { selectedBubbleId, setSelectedBubbleId } = useContext(SelectedChatBubbleContext)

    const handleClick = (index: number) => {
      setSelectedBubbleId(index)
    }

    const comments = useQuery(['comments', { contactId: ccrId, evalId }], () => getComments(ccrId, evalId)).data ?? []

    // create the contents of the map
    useEffect(() => {
      const pins: MessagePin[] = []

      const chatObject = chat?.text?.data?.chatBody ? chat.text.data.chatBody : null
      const messages = chatObject ? chatObject.messages : []
      const earliestMessage = messages[0] && messages.reduce((prev, current) => (prev.sendTimeMs < current.sendTimeMs ? prev : current), messages[0])
      const participants = chatObject ? chatObject.participants : []

      if (!messages?.length || !participants?.length) {
        setPins([])
        return
      }

      // per design, use tinted colors if messages + gap is less than 25 pins; otherwise it becomes too busy
      const useTintedColors = (messages.length * 2) < 25
      const selectedAgentColor = useTintedColors ? ColorScheme.selectedAgentColorTinted : ColorScheme.selectedAgentColor
      const customerColor = useTintedColors ? ColorScheme.customerColorTinted : ColorScheme.customerColor
      const defaultAgentColor = useTintedColors ? ColorScheme.agent_default_color_tinted : ColorScheme.agent_default_color
      const agentColors = useTintedColors ? [...ColorScheme.agent_colors_tinted] : [...ColorScheme.agent_colors]

      // assign each participant a color
      const participantColors = new Array(participants.length)
      participants.forEach((participant: Participant) => {
        const isSelectedAgent = agentAcdId === participant.externalId
        if (isSelectedAgent) {
          participantColors[participant.id] = selectedAgentColor          // selected agent
        } else if (participant.role !== 'agent') {
          participantColors[participant.id] = customerColor               // non agent
        } else {
          const color = agentColors.shift()                               // remove from list so not available anymore
          if (color) {
            participantColors[participant.id] = color                     // secondary agent
          } else {
            participantColors[participant.id] = defaultAgentColor         // no more agent colors available, use default
          }
        }
      })

      messages.forEach((message: Message) => {
        let participant = participants.find((p: { id: number }) => p.id === message.authorId)
        // this should never happen, but just in case...
        if (!participant) {
          participant = { id: 1, role: 'unknown', externalId: 'unknown', label: 'unknown' }
        }
        const isSelectedAgent = agentAcdId === participant.externalId
        pins.push({
          isSelectedAgent,
          color: participantColors[participant.id],
          id: message.id,
          width: Math.min(message.body.length * TEXT_RATIO, MAX_TEXT_WIDTH),
          numLines: Math.ceil(message.body.length / MAX_TEXT_WIDTH),
          timestampInChatMs: message.sendTimeMs - (earliestMessage?.sendTimeMs ?? 0),
          participantName: participant.label,
        })
      })
      setPins(pins)
    }, [chat])

    function commentForPin(pin: MessagePin): EvalComment | undefined {
      return comments.find(comment => comment.contactOffset === pins.indexOf(pin))
    }

    return (
      <Grid container item className={classes.chatMapPanel}>
        {/* @ts-expect-error TODO: upgrade to beam-v11 */}
        <Panel
          titleIcon={<BeamIcon>ChartTimeline</BeamIcon>}
          title={t('textMap')}
          dense
          variant='light'
          classes={{
            body: classes.panelBody,
          }}
          dataTestId={dataTestIdPrefix + 'Panel'}
        >
          {loading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : (
            <ErrorBoundary key={ccrId}>
              {/* @ts-expect-error TODO: upgrade to beam-v11 */}
              <div
                className={classes.pinsContainer}
                data-testid={dataTestIdPrefix + 'PinsContainer'}
                ref={ref}
              >
                {pins ? pins.map((pin, idx) => {
                  return (
                    <>
                      <div
                        key={idx + 'pinrow'}
                        className={classes.pinRow}
                        style={{ flexBasis: FLEX_BASIS_LINE_HEIGHT * pin.numLines }}
                      >
                        <div
                          key={idx + 'pin'}
                          className={classNames(pin.isSelectedAgent ? classes.rightAligned : classes.leftAligned, idx === selectedBubbleId ? classes.isActive : '')}
                          onClick={() => handleClick(idx)}
                          onKeyDown={() => handleClick(idx)}
                          style={{ width: pin.width, backgroundColor: pin.color }}
                          data-testid={dataTestIdPrefix + 'Pin-' + idx}
                          role="button"
                          tabIndex={0}
                        />
                        <div
                          className={classes.commentIcon}
                          role="button"
                          onClick={() => handleClick(idx)}
                          onKeyDown={() => handleClick(idx)}
                          tabIndex={0}
                        >
                          {
                            commentForPin(pin) &&
                            <CommentIcon
                              key={idx + 'comment'}
                              comment={commentForPin(pin)}
                              messageTimestamp={pin.timestampInChatMs}
                            />
                          }
                        </div>
                      </div>
                      {
                        idx < pins.length - 1 && <div
                          key={idx + 'blank'}
                          className={classes.blankLine}
                          style={{ width: pin.width, flexBasis: FLEX_BASIS_BLANK_LINE_HEIGHT }}
                          data-testid={dataTestIdPrefix + 'Pin-Blank-' + idx}
                        />
                      }
                    </>
                  )
                }) : <></>}
              </div>
            </ErrorBoundary>
          )}
        </Panel>
      </Grid>
    )
  }
